import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, ExternalLink, ChevronRight } from "lucide-react";
import { api, type AuditFindingView, type AuditSeverityView, type AuditSourceView } from "../../lib/api.js";

const SOURCE_LABEL: Record<AuditSourceView, string> = {
  mirror: "Content DB pages",
  sitemap: "Whole site (sitemap)",
  crawl: "Whole site (crawl links)",
  all: "Content DB + sitemap",
};

const SEV_ORDER: Record<AuditSeverityView, number> = { high: 0, medium: 1, low: 2 };
const SEV_CLASS: Record<AuditSeverityView, string> = {
  high: "bg-red-50 text-red-700",
  medium: "bg-amber-50 text-amber-800",
  low: "bg-slate-100 text-slate-600",
};
const SEV_BAR: Record<AuditSeverityView, string> = { high: "bg-red-400", medium: "bg-amber-400", low: "bg-slate-300" };

/** Per finding-type: a human label, the action to take, and how to fix it. */
const TYPE_META: Record<string, { label: string; action: string; recommendation: string }> = {
  broken_page: { label: "Broken page (4xx/5xx)", action: "Fix or remove broken pages", recommendation: "These URLs return an error. Fix the slug/redirect or unpublish the page, and update any internal links (including always-link targets) that point here." },
  fetch_error: { label: "Unreachable page", action: "Investigate unreachable pages", recommendation: "The page timed out or failed to load. Check the URL and that the site is reachable." },
  content_not_server_rendered: { label: "Client-side rendered", action: "Content renders client-side (JS)", recommendation: "The server HTML is a shell — the real content loads via JavaScript. Crawlers and AI bots that don't run JS (and this audit's content checks) can't see it. For full content auditing, run a rendered crawl; for SEO, consider SSR or prerendering. Head-level tags (title, description, canonical) are still checked." },
  redirect: { label: "Redirect", action: "Point links at the final URL", recommendation: "Update internal links to the destination URL so visitors and crawlers skip the redirect hop." },
  missing_title: { label: "Missing <title>", action: "Add page titles", recommendation: "Add a unique <title> of 30–60 chars leading with the page's primary keyword." },
  title_length: { label: "Title length", action: "Tune title lengths", recommendation: "Aim for 30–60 chars so titles aren't truncated in search results." },
  duplicate_title: { label: "Duplicate title", action: "Make titles unique", recommendation: "Multiple pages share a title and compete in search. Give each a distinct, keyword-specific title." },
  missing_description: { label: "Missing meta description", action: "Add meta descriptions", recommendation: "Write a unique 120–160 char description with the keyword and a reason to click." },
  description_length: { label: "Meta description length", action: "Tune description lengths", recommendation: "Aim for 120–160 chars so descriptions aren't truncated." },
  duplicate_description: { label: "Duplicate meta description", action: "Make descriptions unique", recommendation: "Shared descriptions weaken each page — write a distinct one per page." },
  missing_h1: { label: "Missing H1", action: "Add an H1 heading", recommendation: "Each page should have exactly one <h1> with its main topic. (Client-rendered pages are excluded from this check automatically.)" },
  multiple_h1: { label: "Multiple H1", action: "Reduce to one H1", recommendation: "Keep a single <h1> per page and demote the rest to <h2>." },
  missing_canonical: { label: "Missing canonical", action: "Add canonical tags", recommendation: "Add a self-referential <link rel=\"canonical\"> to consolidate ranking signals and avoid duplicate-content issues." },
  images_missing_alt: { label: "Images missing alt text", action: "Add image alt text", recommendation: "Add descriptive Hebrew alt text to every content image — for accessibility and image SEO." },
  thin_content: { label: "Thin content", action: "Expand thin pages", recommendation: "Pages under ~300 words of server-rendered text rank poorly. Expand, merge, or noindex them. (Client-rendered pages are excluded from this check automatically.)" },
  missing_og: { label: "Missing Open Graph", action: "Add Open Graph tags", recommendation: "Add og:title / og:description / og:image so shared links render rich previews on social." },
  missing_structured_data: { label: "Missing structured data", action: "Add JSON-LD schema", recommendation: "Add Article / MedicalWebPage / FAQ JSON-LD to qualify for rich results." },
};
const metaFor = (t: string) => TYPE_META[t] ?? { label: t, action: t, recommendation: "" };

function relTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function AuditPage() {
  const qc = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null); // null = latest
  const [source, setSource] = useState<AuditSourceView>("mirror");

  const statusQuery = useQuery({
    queryKey: ["audit", "status"],
    queryFn: () => api.getAuditStatus(),
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2_000 : false),
  });
  const status = statusQuery.data;
  const running = status?.status === "running";

  const runsQuery = useQuery({ queryKey: ["audit", "runs"], queryFn: () => api.listAuditRuns() });
  const runs = runsQuery.data?.runs ?? [];

  // The displayed run: the saved latest, or a past run picked from history. Both
  // read straight from the DB — no crawl happens just to view findings.
  const dataQuery = useQuery({
    queryKey: ["audit", "data", selectedRunId],
    queryFn: () => (selectedRunId == null ? api.getAuditLatest() : api.getAuditRun(selectedRunId)),
  });
  const run = dataQuery.data?.run ?? null;
  const findings = dataQuery.data?.findings ?? [];

  const runMut = useMutation({
    mutationFn: () => api.runAudit({ source }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["audit", "status"] }),
  });

  // When a run finishes, jump back to "latest" and reload saved data.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      setSelectedRunId(null);
      void qc.invalidateQueries({ queryKey: ["audit", "data"] });
      void qc.invalidateQueries({ queryKey: ["audit", "runs"] });
    }
    wasRunning.current = running;
  }, [running, qc]);

  const groups = useMemo(() => groupFindings(findings), [findings]);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold"><ShieldCheck size={20} /> Site Audit</h1>
          <p className="mt-1 text-sm text-muted">
            Crawls your pages and flags SEO/health issues. Pick what to crawl — just Content-DB pages, or the <strong>whole site</strong> via sitemap or link-crawl. Each run is saved; reopen any time to review findings without re-crawling.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {runs.length > 0 && (
            <select
              className="input text-xs"
              value={selectedRunId ?? "latest"}
              onChange={(e) => setSelectedRunId(e.target.value === "latest" ? null : Number(e.target.value))}
              title="View a saved run"
            >
              <option value="latest">Latest · {relTime(runs[0]?.finishedAt ?? runs[0]?.startedAt)}</option>
              {runs.slice(1).map((r) => (
                <option key={r.id} value={r.id}>Run #{r.id} · {SOURCE_LABEL[r.source]} · {relTime(r.finishedAt ?? r.startedAt)}</option>
              ))}
            </select>
          )}
          <select className="input text-xs" value={source} onChange={(e) => setSource(e.target.value as AuditSourceView)} disabled={running} title="Which URLs to crawl">
            {(Object.keys(SOURCE_LABEL) as AuditSourceView[]).map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
          </select>
          <button className="btn-primary" disabled={running || runMut.isPending} onClick={() => runMut.mutate()}>
            {running ? "Running…" : "Run audit"}
          </button>
        </div>
      </header>

      {running && (
        <div className="mb-5 rounded-md border border-line bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Crawling {status?.source ? SOURCE_LABEL[status.source].toLowerCase() : ""}… {status?.crawled ?? 0}{status?.total ? ` / ${status.total}` : ""} pages
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-emerald-100">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: status?.total ? `${Math.round(((status.crawled ?? 0) / status.total) * 100)}%` : "10%" }} />
          </div>
        </div>
      )}
      {status?.status === "error" && (
        <div className="mb-5 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">Audit failed: {status.error}</div>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Pages crawled" value={run?.pagesCrawled ?? 0} />
        <Kpi label="High" value={run?.highCount ?? 0} tone="high" />
        <Kpi label="Medium" value={run?.mediumCount ?? 0} tone="medium" />
        <Kpi label="Low" value={run?.lowCount ?? 0} tone="low" />
      </div>
      <p className="mb-4 text-xs text-muted">
        {run
          ? <>Showing {selectedRunId == null ? "the latest run" : `run #${run.id}`} · {SOURCE_LABEL[run.source]} · {relTime(run.finishedAt ?? run.startedAt)} · {run.findingsTotal} findings across {run.pagesCrawled} pages (saved)</>
          : "No audit has run yet — pick a source and click Run audit."}
      </p>

      {dataQuery.isPending ? (
        <p className="text-sm text-muted">Loading saved findings…</p>
      ) : groups.length === 0 ? (
        run ? <p className="rounded-md border border-line bg-white p-6 text-center text-sm text-muted">No issues found. 🎉</p> : null
      ) : (
        <>
          <h2 className="mb-2 text-sm font-semibold">Recommended actions <span className="font-normal text-muted">· prioritized by severity + reach</span></h2>
          <div className="space-y-2">
            {groups.map((g) => <ActionCard key={g.type} group={g} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: AuditSeverityView }) {
  const cls = tone && value > 0 ? SEV_CLASS[tone] : "bg-white text-ink";
  return (
    <div className={`rounded-md border border-line p-3 ${cls}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs opacity-80">{label}</div>
    </div>
  );
}

interface Group { type: string; severity: AuditSeverityView; items: AuditFindingView[] }

function groupFindings(findings: AuditFindingView[]): Group[] {
  const byType = new Map<string, Group>();
  for (const f of findings) {
    const g = byType.get(f.type) ?? { type: f.type, severity: f.severity, items: [] };
    g.items.push(f);
    if (SEV_ORDER[f.severity] < SEV_ORDER[g.severity]) g.severity = f.severity;
    byType.set(f.type, g);
  }
  // Prioritize: most-severe first, then by how many pages it affects.
  return Array.from(byType.values()).sort(
    (a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity] || b.items.length - a.items.length,
  );
}

function ActionCard({ group }: { group: Group }) {
  const [open, setOpen] = useState(false);
  const meta = metaFor(group.type);
  return (
    <div className="overflow-hidden rounded-md border border-line bg-white">
      <div className={`h-0.5 w-full ${SEV_BAR[group.severity]}`} />
      <button className="flex w-full items-start gap-2 px-3 py-2.5 text-left" onClick={() => setOpen((o) => !o)}>
        <ChevronRight size={15} className={`mt-0.5 shrink-0 text-muted transition-transform ${open ? "rotate-90" : ""}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${SEV_CLASS[group.severity]}`}>{group.severity}</span>
            <span className="text-sm font-medium">{meta.action}</span>
            <span className="ml-auto whitespace-nowrap text-xs text-muted">{group.items.length} {group.items.length === 1 ? "page" : "pages"}</span>
          </div>
          {meta.recommendation && <p className="mt-1 text-xs text-muted" dir="auto">{meta.recommendation}</p>}
        </div>
      </button>
      {open && (
        <ul className="border-t border-line/60 px-3 py-2">
          {group.items.slice(0, 300).map((f) => (
            <li key={f.id} className="border-b border-line/40 py-1.5 last:border-0">
              <a href={f.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline" dir="ltr">
                {f.url.replace(/^https?:\/\//, "")} <ExternalLink size={11} className="shrink-0" />
              </a>
              <div className="text-xs text-muted" dir="auto">{f.message}</div>
            </li>
          ))}
          {group.items.length > 300 && <li className="py-1 text-2xs text-muted">+{group.items.length - 300} more</li>}
        </ul>
      )}
    </div>
  );
}
